import React, { useEffect, useState } from "react";
import { FaFolder, FaFolderOpen, FaChartBar } from "react-icons/fa";
import LoadingScreen from "../LoadingScreen";
import DashboardViewerModal from "./DashboardViewerModal";
import "../../styles/DashboardsView.css";

import {
  fetchDashboards,
  requestDashboardAccess,
} from "../../services/dashboardsApi";

export default function DashboardsView() {
  const [loading, setLoading] = useState(false);
  const [dashboards, setDashboards] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [embedUrl, setEmbedUrl] = useState("");
  const [embedLoading, setEmbedLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState(null);

  // ✅ carpeta seleccionada (category)
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchDashboards()
      .then((data) => {
        const items = Array.isArray(data) ? data : data.items || [];
        setDashboards(items);
      })
      .catch((e) => console.error("❌ Error cargando dashboards:", e))
      .finally(() => setLoading(false));
  }, []);

  const openDashboard = async (d) => {
    setSelected(d);
    setIsOpen(true);
    setEmbedUrl("");
    setExpiresIn(null);
    setEmbedLoading(true);

    try {
      const dashboardId = d.dashboard_id;
      const data = await requestDashboardAccess(dashboardId);
      setEmbedUrl(data.url);
      setExpiresIn(data.expires_in ?? null);
    } catch (e) {
      console.error("❌ Error abriendo dashboard:", e);
    } finally {
      setEmbedLoading(false);
    }
  };

  const refreshAccess = async () => {
    if (!selected?.dashboard_id) return;

    setEmbedLoading(true);

    try {
      const data = await requestDashboardAccess(selected.dashboard_id);
      setEmbedUrl(data.url);
      setExpiresIn(data.expires_in ?? null);
    } catch (e) {
      console.error("❌ Error refrescando access:", e);
    } finally {
      setEmbedLoading(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelected(null);
    setEmbedUrl("");
    setExpiresIn(null);
    setEmbedLoading(false);
  };

  // ✅ agrupar por category
  const normalizeCategory = (c) => (c || "Sin categoría").trim();

  const grouped = dashboards.reduce((acc, d) => {
    const cat = normalizeCategory(d.category);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(d);
    return acc;
  }, {});

  const categories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  if (loading) return <LoadingScreen text="Cargando tableros..." />;

  return (
    <div className="dashboards-view">
      <div className="dashboards-header">
        <h2>Tableros</h2>
        <p>Visualiza tableros de QuickSight.</p>
      </div>

      {/* ✅ Vista de carpetas */}
      {!selectedCategory && (
        <div className="dashboards-grid">
          {categories.map((cat) => (
            <button
              key={cat}
              className="dashboard-card dashboard-folder"
              onClick={() => setSelectedCategory(cat)}
            >
              <div className="dashboard-info">
                <h3 className="dashboard-folder-name">
                  <FaFolder className="icon-folder" />
                  <span>{cat}</span>
                </h3>

                <div className="dashboard-meta">
                  <span className="dashboard-badge">
                    {grouped[cat].length} tablero
                    {grouped[cat].length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
            </button>
          ))}

          {!categories.length && (
            <div className="dashboards-empty">No hay tableros disponibles.</div>
          )}
        </div>
      )}

      {/* ✅ Vista dentro de carpeta */}
      {selectedCategory && (
        <>
          <div className="dashboards-folder-header">
            <button
              className="dashboards-back"
              onClick={() => setSelectedCategory(null)}
            >
              ← Volver
            </button>

            <h3 className="dashboards-folder-title">
              <FaFolderOpen className="icon-folder open" />
              <span>{selectedCategory}</span>
            </h3>
          </div>

          <div className="dashboards-grid">
            {(grouped[selectedCategory] || []).map((d) => (
              <button
                key={d.dashboard_id}
                className="dashboard-card"
                onClick={() => openDashboard(d)}
              >
                <div className="dashboard-info">
                  <h3 className="dashboard-card-title">
                    <FaChartBar className="dashboard-card-icon" />
                    <span>{d.title || d.dashboard_id}</span>
                  </h3>

                  <div className="dashboard-meta">
                    {!!d.status && (
                      <span
                        className={`dashboard-status ${String(
                          d.status
                        ).toLowerCase()}`}
                      >
                        {d.status}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <DashboardViewerModal
        isOpen={isOpen}
        onClose={closeModal}
        dashboard={selected}
        embedUrl={embedUrl}
        isLoading={embedLoading}
        expiresIn={expiresIn}
        onRefresh={refreshAccess}
      />
    </div>
  );
}
